import React, { useState, useEffect } from 'react';
import { Card, Button, Typography, Tag, Table, Modal, Form, Input, Space, Alert, Tabs } from 'antd';
import { useToast } from '../../../contexts/ToastContext';
import { CrownOutlined, CheckCircleOutlined, CloseCircleOutlined, CreditCardOutlined, ShoppingOutlined } from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../../services/api';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import { getApiErrorMessage } from '../../../utils/apiErrorMessage';
import { getParamPaymentUrl, redirectToParam3d } from '../../../utils/subscriptionPaymentRedirect';
import { isRecoverableSubscriptionPaymentMessage } from '../../../utils/subscriptionRecoverablePaymentMessage';
import SubscriptionPaymentIssueModal from '../../../components/Common/SubscriptionPaymentIssueModal';
import { CreateSubscriptionRequest, RenewSubscriptionRequest, SubscriptionType } from '../../../types';
import CreditCard from '../../../components/Common/CreditCard';
import dayjs from 'dayjs';
import { useDevFakePaymentEnabled } from '../../../hooks/useDevFakePaymentEnabled';
import { useAuth } from '../../../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

const SubscriptionTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { syncAuthAfterSubscription } = useAuth();
  const { data: devFakeOn } = useDevFakePaymentEnabled();
  const [searchParams, setSearchParams] = useSearchParams();
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [isRenewal, setIsRenewal] = useState(false);
  const [selectedType, setSelectedType] = useState<SubscriptionType>(SubscriptionType.Monthly);
  const [form] = Form.useForm();
  const [cardNumber, setCardNumber] = useState('');
  const [cardHolderName, setCardHolderName] = useState('');
  const [expiryDate, setExpiryDate] = useState('');
  const [cvv, setCvv] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentIssueOpen, setPaymentIssueOpen] = useState(false);
  const [paymentIssueMessage, setPaymentIssueMessage] = useState('');

  const { data: currentSubscription, isLoading: subscriptionLoading, refetch: refetchCurrent } = useQuery({
    queryKey: companyProfileQueryKeys.subscription,
    queryFn: async () => {
      const r = await apiService.getCurrentSubscription();
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: subscriptionHistory = [], isLoading: historyLoading } = useQuery({
    queryKey: companyProfileQueryKeys.subscriptionHistory,
    queryFn: async () => {
      const r = await apiService.getSubscriptionHistory();
      if (!r.success) throw new Error(r.message);
      return r.data ?? [];
    },
    staleTime: 1000 * 60 * 2,
  });

  const { data: pricingData, isLoading: pricingLoading } = useQuery({
    queryKey: ['subscription-pricing'],
    queryFn: async () => {
      const r = await apiService.getSubscriptionPricing();
      if (!r.success) throw new Error(r.message);
      return r.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const monthlyPrice = pricingData?.monthlyPrice ?? 5;
  const yearlyPrice = pricingData?.yearlyPrice ?? 10;
  const yearlyDiscount = monthlyPrice > 0
    ? Math.max(0, Math.round((1 - yearlyPrice / (monthlyPrice * 12)) * 100))
    : 0;

  const cancelMutation = useMutation({
    mutationFn: () => apiService.cancelSubscription(),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Abonelik iptal edildi.');
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscription });
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscriptionHistory });
        void syncAuthAfterSubscription();
      } else {
        toast.error(response.message || 'Abonelik iptal edilirken bir hata oluştu.');
      }
    },
    onError: () => toast.error('Abonelik iptal edilirken bir hata oluştu.'),
  });

  useEffect(() => {
    const status = searchParams.get('status');
    const statusMessage = searchParams.get('message');
    if (status === 'success') {
      toast.success(statusMessage || 'Abonelik başarıyla oluşturuldu!');
      setSearchParams({}, { replace: true });
      refetchCurrent();
      queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscription });
      queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscriptionHistory });
      void syncAuthAfterSubscription();
    } else if (status === 'error') {
      toast.error(statusMessage || 'Ödeme işlemi başarısız oldu.');
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams, refetchCurrent, queryClient, syncAuthAfterSubscription]);

  useEffect(() => {
    const err = searchParams.get('paymentInputError');
    if (err) {
      setPaymentIssueMessage(decodeURIComponent(err));
      setPaymentIssueOpen(true);
      setPaymentModalVisible(true);
      const next = new URLSearchParams(searchParams);
      next.delete('paymentInputError');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubscribe = (type: SubscriptionType) => {
    setSelectedType(type);
    setIsRenewal(false);
    setPaymentModalVisible(true);
    form.resetFields();
    setCardNumber('');
    setCardHolderName('');
    setExpiryDate('');
    setCvv('');
  };

  const handleRenew = () => {
    if (!currentSubscription) return;
    setSelectedType(currentSubscription.subscriptionType);
    setIsRenewal(true);
    setPaymentModalVisible(true);
    form.resetFields();
    setCardNumber('');
    setCardHolderName('');
    setExpiryDate('');
    setCvv('');
  };

  const handlePayment = async (values: any) => {
    try {
      setPaymentLoading(true);

      // Expiry date'i ay ve yıl olarak ayır
      let expiryMonth = '';
      let expiryYear = '';
      
      if (expiryDate) {
        // MM/YY veya MM/YYYY formatından ayır
        const parts = expiryDate.split('/');
        if (parts.length === 2) {
          expiryMonth = parts[0].padStart(2, '0');
          expiryYear = parts[1].length === 4 ? parts[1].substring(2) : parts[1];
        }
      }

      if (isRenewal) {
        const request: RenewSubscriptionRequest = {
          subscriptionType: selectedType,
          cardNumber: cardNumber.replace(/\s/g, ''), // Boşlukları temizle
          cardHolderName: cardHolderName,
          expiryMonth: expiryMonth,
          expiryYear: expiryYear,
          cvv: cvv,
          phoneNumber: values.phoneNumber
        };

        const response = await apiService.renewSubscription(request);
        const paymentUrl = getParamPaymentUrl(response);
        const orderId =
          response.data?.orderId ?? (response.data as { OrderId?: string } | undefined)?.OrderId;
        if (response.success && paymentUrl) {
          if (orderId) {
            localStorage.setItem('pendingPaymentOrderId', orderId);
            localStorage.removeItem('pendingAdvertisementPaymentOrderId');
          }
          redirectToParam3d(paymentUrl);
        } else {
          const errMsg =
            response.message ||
            (response.success && !paymentUrl
              ? '3D yönlendirme adresi alınamadı. Yanıtı kontrol edin veya tekrar deneyin.'
              : 'Ödeme işlemi başlatılamadı.');
          if (isRecoverableSubscriptionPaymentMessage(errMsg)) {
            setPaymentIssueMessage(errMsg);
            setPaymentIssueOpen(true);
          } else {
            toast.error(errMsg);
          }
          setPaymentLoading(false);
        }
      } else {
        const request: CreateSubscriptionRequest = {
          subscriptionType: selectedType,
          cardNumber: cardNumber.replace(/\s/g, ''), // Boşlukları temizle
          cardHolderName: cardHolderName,
          expiryMonth: expiryMonth,
          expiryYear: expiryYear,
          cvv: cvv,
          phoneNumber: values.phoneNumber
        };

        const response = await apiService.createSubscription(request);
        const paymentUrl = getParamPaymentUrl(response);
        const orderId =
          response.data?.orderId ?? (response.data as { OrderId?: string } | undefined)?.OrderId;
        if (response.success && paymentUrl) {
          if (orderId) {
            localStorage.setItem('pendingPaymentOrderId', orderId);
            localStorage.removeItem('pendingAdvertisementPaymentOrderId');
          }
          redirectToParam3d(paymentUrl);
        } else {
          const errMsg =
            response.message ||
            (response.success && !paymentUrl
              ? '3D yönlendirme adresi alınamadı. Yanıtı kontrol edin veya tekrar deneyin.'
              : 'Ödeme işlemi başlatılamadı.');
          if (isRecoverableSubscriptionPaymentMessage(errMsg)) {
            setPaymentIssueMessage(errMsg);
            setPaymentIssueOpen(true);
          } else {
            toast.error(errMsg);
          }
          setPaymentLoading(false);
        }
      }
    } catch (error) {
      console.error('Ödeme işlemi sırasında hata:', error);
      const errMsg = getApiErrorMessage(error, 'Ödeme işlemi sırasında bir hata oluştu.');
      if (isRecoverableSubscriptionPaymentMessage(errMsg)) {
        setPaymentIssueMessage(errMsg);
        setPaymentIssueOpen(true);
      } else {
        toast.error(errMsg);
      }
      setPaymentLoading(false);
    }
  };

  const handleDevFakePayment = async () => {
    try {
      setPaymentLoading(true);
      const res = await apiService.devFakeCompleteSubscription({
        subscriptionType: selectedType,
        isRenewal,
      });
      if (res.success && res.data) {
        toast.success(res.data.message || res.message || 'Test ödemesi tamamlandı.');
        setPaymentModalVisible(false);
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscription });
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.subscriptionHistory });
        await syncAuthAfterSubscription();
      } else {
        toast.error(res.message || 'Test ödemesi başarısız.');
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Test ödemesi isteği başarısız.'));
    } finally {
      setPaymentLoading(false);
    }
  };

  const handleCancel = () => {
    cancelMutation.mutate();
  };

  const getSubscriptionTypeName = (type: SubscriptionType): string => {
    switch (type) {
      case SubscriptionType.Free:
        return 'Ücretsiz';
      case SubscriptionType.Monthly:
        return 'Aylık';
      case SubscriptionType.Yearly:
        return 'Yıllık';
      default:
        return 'Bilinmiyor';
    }
  };

  const getPrice = (type: SubscriptionType): number => {
    switch (type) {
      case SubscriptionType.Monthly:
        return monthlyPrice;
      case SubscriptionType.Yearly:
        return yearlyPrice;
      default:
        return 0;
    }
  };

  const historyColumns = [
    {
      title: 'Abonelik Tipi',
      dataIndex: 'subscriptionType',
      key: 'subscriptionType',
      render: (type: SubscriptionType) => (
        <Tag color={type === SubscriptionType.Yearly ? 'gold' : 'blue'}>
          {getSubscriptionTypeName(type)}
        </Tag>
      )
    },
    {
      title: 'Başlangıç',
      dataIndex: 'startDate',
      key: 'startDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY')
    },
    {
      title: 'Bitiş',
      dataIndex: 'endDate',
      key: 'endDate',
      render: (date: string) => dayjs(date).format('DD.MM.YYYY')
    },
    {
      title: 'Tutar',
      dataIndex: 'amount',
      key: 'amount',
      render: (amount: number) => `₺${amount.toFixed(2)}`
    },
    {
      title: 'Durum',
      dataIndex: 'paymentStatus',
      key: 'paymentStatus',
      render: (status: string) => (
        <Tag color={status === 'Completed' ? 'green' : status === 'Failed' ? 'red' : 'orange'}>
          {status === 'Completed' ? 'Tamamlandı' : status === 'Failed' ? 'Başarısız' : 'Beklemede'}
        </Tag>
      )
    },
    {
      title: 'Aktif',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        isActive ? <CheckCircleOutlined style={{ color: 'var(--success)' }} /> : <CloseCircleOutlined style={{ color: 'var(--error)' }} />
      )
    }
  ];

  return (
    <div className="panel-tab-content">
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Title level={2} style={{ marginBottom: '24px' }}>
          <CrownOutlined style={{ marginRight: '8px' }} />
          Abonelik Yönetimi
        </Title>

        {/* Mevcut Abonelik */}
        <Card style={{ marginBottom: '24px' }} loading={subscriptionLoading || pricingLoading}>
          <Title level={4}>Mevcut Abonelik</Title>
          {currentSubscription ? (
            <div>
              {currentSubscription.isActive && !currentSubscription.isExpired ? (
                <Alert
                  message="Aktif Abonelik"
                  description={
                    <div>
                      <p><strong>Tip:</strong> {getSubscriptionTypeName(currentSubscription.subscriptionType)}</p>
                      {currentSubscription.startDate && (
                        <p><strong>Başlangıç:</strong> {dayjs(currentSubscription.startDate).format('DD.MM.YYYY')}</p>
                      )}
                      {currentSubscription.endDate && (
                        <p><strong>Bitiş:</strong> {dayjs(currentSubscription.endDate).format('DD.MM.YYYY')}</p>
                      )}
                      <p><strong>Kalan Gün:</strong> {currentSubscription.daysRemaining} gün</p>
                    </div>
                  }
                  type="success"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              ) : (
                <Alert
                  message="Aktif Abonelik Yok"
                  description="Panel sayfalarına erişmek için abonelik gereklidir."
                  type="warning"
                  showIcon
                  style={{ marginBottom: '16px' }}
                />
              )}

              <Space>
                {currentSubscription.isActive && !currentSubscription.isExpired && (
                  <Button type="primary" onClick={handleRenew}>
                    Aboneliği Yenile
                  </Button>
                )}
                {currentSubscription.isActive && !currentSubscription.isExpired && (
                  <Button danger onClick={handleCancel} loading={cancelMutation.isPending}>
                    İptal Et
                  </Button>
                )}
              </Space>
            </div>
          ) : (
            <Text type="secondary">Abonelik bilgisi yükleniyor...</Text>
          )}
        </Card>

        {/* Yeni Abonelik */}
        {(!currentSubscription || !currentSubscription.isActive || currentSubscription.isExpired) && (
          <Card style={{ marginBottom: '24px' }}>
            <Title level={4}>Yeni Abonelik</Title>
            <Paragraph>
              Panel sayfalarına erişmek için aylık veya yıllık abonelik satın alabilirsiniz.
            </Paragraph>
            <Space size="large">
              <Card
                hoverable
                style={{ width: 300, textAlign: 'center' }}
                actions={[
                  <Button
                    type="primary"
                    key="subscribe"
                    onClick={() => handleSubscribe(SubscriptionType.Monthly)}
                  >
                    Abone Ol
                  </Button>
                ]}
              >
                <Title level={3}>Aylık</Title>
                <Title level={2} style={{ color: 'var(--primary-500)' }}>₺{monthlyPrice.toFixed(2)}</Title>
                <Text type="secondary">/ay</Text>
              </Card>

              <Card
                hoverable
                style={{ width: 300, textAlign: 'center', border: '2px solid #ffd700' }}
                actions={[
                  <Button
                    type="primary"
                    key="subscribe"
                    onClick={() => handleSubscribe(SubscriptionType.Yearly)}
                    style={{ backgroundColor: '#ffd700', borderColor: '#ffd700', color: '#000' }}
                  >
                    Abone Ol
                  </Button>
                ]}
              >
                <Title level={3}>Yıllık</Title>
                <Title level={2} style={{ color: '#ffd700' }}>₺{yearlyPrice.toFixed(2)}</Title>
                <Text type="secondary">/yıl</Text>
                <div style={{ marginTop: '8px' }}>
                  <Tag color="gold">%{yearlyDiscount} İndirim</Tag>
                </div>
              </Card>
            </Space>
          </Card>
        )}

        {/* Abonelik Geçmişi ve Siparişlerim */}
        <Card>
          <Tabs defaultActiveKey="history">
            <TabPane 
              tab={<span><CreditCardOutlined /> Abonelik Geçmişi</span>} 
              key="history"
            >
              <Table
                scroll={{ x: 'max-content' }}
                columns={historyColumns}
                dataSource={subscriptionHistory}
                rowKey="id"
                loading={historyLoading}
                pagination={{ pageSize: 10 }}
              />
            </TabPane>
            <TabPane 
              tab={<span><ShoppingOutlined /> Siparişlerim</span>} 
              key="orders"
            >
              <Table
                scroll={{ x: 'max-content' }}
                columns={historyColumns}
                dataSource={subscriptionHistory}
                rowKey="id"
                loading={historyLoading}
                pagination={{ pageSize: 10 }}
                title={() => <Text strong>Tüm satın alma işlemleriniz</Text>}
              />
            </TabPane>
          </Tabs>
        </Card>

        {/* Ödeme Modal */}
        <Modal
          title={
            <div>
              <CreditCardOutlined style={{ marginRight: '8px' }} />
              {isRenewal ? 'Aboneliği Yenile' : 'Abonelik Ödemesi'}
            </div>
          }
          open={paymentModalVisible}
          onCancel={() => {
            setPaymentModalVisible(false);
            form.resetFields();
            setCardNumber('');
            setCardHolderName('');
            setExpiryDate('');
            setCvv('');
          }}
          footer={null}
          width={700}
        >
          <Alert
            message="Güvenli Ödeme"
            description="Ödeme işlemi Param ödeme sistemi üzerinden 3D Secure ile güvenli bir şekilde gerçekleştirilecektir."
            type="info"
            showIcon
            style={{ marginBottom: '16px' }}
          />
          
          <Alert
            message="Gizlilik ve Güvenlik"
            description={
              <div>
                <strong>Menteem.com.tr kart bilgilerinizi asla saklamaz.</strong>
                <br />
                Tüm ödeme işlemleri Param ödeme altyapısı üzerinden güvenli bir şekilde gerçekleştirilmektedir.
              </div>
            }
            type="success"
            showIcon
            icon={<span style={{ fontSize: '16px' }}>🔒</span>}
            style={{ marginBottom: '16px', backgroundColor: '#f6ffed', borderColor: '#b7eb8f' }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={handlePayment}
          >
            <Form.Item label="Abonelik Tipi">
              <Input
                value={getSubscriptionTypeName(selectedType)}
                disabled
                style={{ fontWeight: 'bold' }}
              />
            </Form.Item>

            <Form.Item label="Tutar">
              <Input
                value={`₺${getPrice(selectedType).toFixed(2)}`}
                disabled
                style={{ fontWeight: 'bold', fontSize: '18px', color: 'var(--primary-500)' }}
              />
            </Form.Item>

            <CreditCard
              cardNumber={cardNumber}
              cardHolderName={cardHolderName}
              expiryDate={expiryDate}
              cvv={cvv}
              onCardNumberChange={setCardNumber}
              onCardHolderChange={setCardHolderName}
              onExpiryChange={setExpiryDate}
              onCvvChange={setCvv}
              onCvvFocus={() => {}}
              onCvvBlur={() => {}}
            />

            <Form.Item
              rules={[
                { required: true, message: 'Kart numarası giriniz' },
                { pattern: /^[0-9\s]{13,19}$/, message: 'Geçerli bir kart numarası giriniz' }
              ]}
              style={{ display: 'none' }}
            >
              <Input name="cardNumber" value={cardNumber} />
            </Form.Item>

            <Form.Item
              rules={[{ required: true, message: 'Kart sahibi adını giriniz' }]}
              style={{ display: 'none' }}
            >
              <Input name="cardHolderName" value={cardHolderName} />
            </Form.Item>

            <Form.Item
              rules={[
                { required: true, message: 'Son kullanma tarihi giriniz' },
                { pattern: /^(0[1-9]|1[0-2])\/([0-9]{2})$/, message: 'MM/YY formatında giriniz' }
              ]}
              style={{ display: 'none' }}
            >
              <Input name="expiryDate" value={expiryDate} />
            </Form.Item>

            <Form.Item
              rules={[
                { required: true, message: 'CVV giriniz' },
                { pattern: /^[0-9]{3,4}$/, message: 'Geçerli bir CVV giriniz' }
              ]}
              style={{ display: 'none' }}
            >
              <Input name="cvv" value={cvv} />
            </Form.Item>

            <Form.Item
              name="phoneNumber"
              label="Telefon Numarası (İsteğe Bağlı)"
              rules={[
                { pattern: /^[0-9]{10,11}$/, message: 'Geçerli bir telefon numarası giriniz' }
              ]}
            >
              <Input placeholder="5XX XXX XX XX" />
            </Form.Item>

            <Form.Item>
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  block 
                  loading={paymentLoading}
                  disabled={!cardNumber || !cardHolderName || !expiryDate || !cvv}
                >
                  Ödemeyi Tamamla
                </Button>
                {devFakeOn && (
                  <Button
                    type="dashed"
                    danger
                    block
                    htmlType="button"
                    loading={paymentLoading}
                    onClick={handleDevFakePayment}
                  >
                    Test: Param olmadan tamamla
                  </Button>
                )}
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        <SubscriptionPaymentIssueModal
          open={paymentIssueOpen}
          message={paymentIssueMessage}
          onClose={() => setPaymentIssueOpen(false)}
        />
      </div>
    </div>
  );
};

export default SubscriptionTab;

